// ============================================================================
// Lazy Initialization of HttpClient singleton
// ============================================================================

import { HttpClient } from '@sisense/sdk-rest-client';
import OpenAI from 'openai';
import {
	AuthenticatorConfig,
	createHttpClientFromAuthenticator,
} from './utils/http-client';
import { createHttpClientFromConfig } from './utils/http-client';
import { createOpenAIClient } from './utils/openai-client';

let httpClientInstance: HttpClient | null = null;

/**
 * Set a pre-created HttpClient instance (similar to initializeSisenseServices)
 * Useful when the consuming app already has an HttpClient instance
 *
 * @param httpClient - Pre-created HttpClient instance
 */
export function initializeHttpClient(httpClient: HttpClient): void {
	httpClientInstance = httpClient;
}

/**
 * Get the shared HttpClient instance (lazy initialization)
 *
 * @returns The HttpClient instance
 * @throws Error if client hasn't been initialized
 */
export function getHttpClient(): HttpClient {
	if (!httpClientInstance) {
		throw new Error(
			'HttpClient not initialized. Create one using createHttpClient() and then initialize it using initializeHttpClient().',
		);
	}
	return httpClientInstance;
}

/**
 * Reset the http client
 * This function is useful when the consuming app needs to switch tenant or login to another user
 */
export function resetHttpClient(): void {
	const currentHttpClient = getHttpClient();
	const url = currentHttpClient.url;
	const authenticator = currentHttpClient.auth;
	const httpClient = createHttpClientFromAuthenticator(url, authenticator);
	initializeHttpClient(httpClient);
}

// ============================================================================
// Lazy Initialization of OpenAI client singleton
// ============================================================================

let openAIClientInstance: OpenAI | null = null;

/**
 * Set a pre-created OpenAI client instance (similar to initializeSisenseServices)
 * Useful when the consuming app already has an OpenAI client instance
 *
 * @param openAIClient - Pre-created OpenAI client instance
 */
export function initializeOpenAIClient(openAIClient: OpenAI): void {
	openAIClientInstance = openAIClient;
}

/**
 * Get the shared OpenAI client instance (lazy initialization)
 *
 * @returns The OpenAI client instance
 * @throws Error if client hasn't been initialized
 */
export function getOpenAIClient(): OpenAI {
	if (!openAIClientInstance) {
		throw new Error(
			'OpenAI client not initialized. Create one using createOpenAIClient() and then initialize it using initializeOpenAIClient().',
		);
	}
	return openAIClientInstance;
}

/**
 * Reset the OpenAI client (useful for testing)
 *
 * Allows tests to clean up or reset the shared instance between test runs
 */
export function resetOpenAIClient(): void {
	const openAIClient = createOpenAIClient(getHttpClient());
	initializeOpenAIClient(openAIClient);
}

/**
 * Initialize the HttpClient and OpenAI client singletons
 * @param authConfig - Authenticator configuration
 */
export function initializeClients(authConfig: AuthenticatorConfig): void {
	// Create and initialize HttpClient singleton
	const httpClient = createHttpClientFromConfig(authConfig);
	initializeHttpClient(httpClient);

	// Create and initialize OpenAI client singleton
	const openAIClient = createOpenAIClient(httpClient);
	initializeOpenAIClient(openAIClient);
}
